import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { mediaUrl } from "@/lib/api";
import Lightbox from "@/components/marketing/Lightbox";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lb, setLb] = useState({ open: false, index: 0 });

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/projects/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="bg-ink text-ivory min-h-screen pt-40 px-6 md:px-12">
        <p className="font-editorial text-4xl italic" data-testid="project-not-found">Project not found.</p>
        <Link to="/portfolio" className="btn-outline-ivory mt-8" data-testid="project-back-portfolio">Back to portfolio</Link>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="bg-ink text-ivory min-h-screen pt-40 px-6 md:px-12" data-testid="project-loading">
        <p className="label-mono opacity-60">Loading…</p>
      </main>
    );
  }

  const { project, prev_slug, next_slug } = data;
  const gallery = project.gallery || [];

  return (
    <main data-testid="project-detail" className="bg-ink text-ivory pt-28 md:pt-32 pb-32">
      <section className="relative w-full h-[80vh] min-h-[500px] overflow-hidden bg-neutral-900">
        <motion.img
          src={mediaUrl(project.cover_image || gallery[0])}
          alt={project.title}
          data-testid="project-cover-image"
          onError={(e) => {
            const alt = mediaUrl(gallery[0]);
            if (alt && e.currentTarget.src !== alt) e.currentTarget.src = alt;
            else e.currentTarget.style.display = "none";
          }}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-12 md:pb-20">
          <p className="label-mono opacity-80">{project.category_slug?.replace("-", " ")}</p>
          <h1 className="mt-4 font-editorial text-6xl md:text-8xl lg:text-9xl leading-[0.95]">
            {project.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 label-mono opacity-80">
            <span>{project.location}</span>
            <span>{project.date_or_year}</span>
          </div>
        </div>
      </section>

      {project.description && (
        <section className="px-6 md:px-12 lg:px-16 mt-20 md:mt-28 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-3">
            <p className="label-mono opacity-60">About</p>
          </div>
          <p className="col-span-12 md:col-span-8 font-editorial text-3xl md:text-4xl leading-[1.2] italic">
            {project.description}
          </p>
        </section>
      )}

      <section className="px-6 md:px-12 lg:px-16 mt-24 columns-1 sm:columns-2 gap-4 md:gap-6 [column-fill:_balance]">
        {gallery.map((src, i) => (
          <motion.button
            key={i}
            type="button"
            onClick={() => setLb({ open: true, index: i })}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, delay: (i % 4) * 0.05, ease: [0.19, 1, 0.22, 1] }}
            className="text-left w-full mb-4 md:mb-6 break-inside-avoid block"
            data-testid={`project-gallery-item-${i}`}
          >
            <div className="photo-frame bg-neutral-900">
              <img
                src={mediaUrl(src)}
                alt={`${project.title} ${i + 1}`}
                loading="lazy"
                className="w-full h-auto block"
              />
            </div>
          </motion.button>
        ))}
      </section>

      <section className="px-6 md:px-12 lg:px-16 mt-32 border-t border-ivory/10 pt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <div className="flex gap-8 items-center">
          {prev_slug && (
            <Link to={`/portfolio/${prev_slug}`} className="hover-underline label-mono" data-testid="project-prev">
              ← Previous
            </Link>
          )}
          {next_slug && (
            <Link to={`/portfolio/${next_slug}`} className="hover-underline label-mono" data-testid="project-next">
              Next →
            </Link>
          )}
        </div>
        <Link
          to={`/booking?category=${encodeURIComponent(project.category_slug)}`}
          className="btn-outline-ivory"
          data-testid="project-enquire-similar"
        >
          Enquire about a similar shoot
        </Link>
      </section>

      <Lightbox
        images={gallery}
        open={lb.open}
        index={lb.index}
        onClose={() => setLb({ open: false, index: 0 })}
      />
    </main>
  );
}
